import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, CardActionArea, IconButton,
  CircularProgress, Chip, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, Paper, alpha, useTheme, Stack,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EditNoteIcon from '@mui/icons-material/EditNote';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import { useNavigate } from 'react-router-dom';
import * as flowService from '../../services/documentFlowService';
import type { FlowDraft } from '../../services/documentFlowService';

interface DraftsListProps { maxItems?: number; compact?: boolean; }

const DraftsList: React.FC<DraftsListProps> = ({ maxItems, compact = false }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [drafts, setDrafts] = useState<FlowDraft[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState<string | null>(null);

  useEffect(() => { loadDrafts(); }, []);

  const loadDrafts = async () => {
    setLoading(true);
    try { const r = await flowService.listDrafts({ limit: maxItems || 20 }); setDrafts(r.drafts); }
    catch {} finally { setLoading(false); }
  };

  const handleDelete = async (draftId: string) => {
    try { await flowService.deleteDraft(draftId); setDrafts(prev => prev.filter(d => d.id !== draftId)); }
    catch {} setDeleteDialog(null);
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={24} /></Box>;

  if (drafts.length === 0) {
    return (
      <Paper variant="outlined" sx={{ py: compact ? 2 : 4, px: 2, textAlign: 'center', borderRadius: 2, borderStyle: 'dashed' }}>
        <FolderOpenIcon sx={{ fontSize: compact ? 28 : 40, color: 'action.disabled', mb: 0.5 }} />
        <Typography variant={compact ? 'caption' : 'body2'} color="text.secondary">Nenhum rascunho pendente</Typography>
      </Paper>
    );
  }

  const displayed = maxItems ? drafts.slice(0, maxItems) : drafts;

  return (
    <Stack spacing={compact ? 0.8 : 1.2}>
      {displayed.map(draft => {
        const progress = draft.total_steps ? ((draft.current_step + 1) / draft.total_steps) * 100 : 0;
        return (
          <Card key={draft.id} variant="outlined"
            sx={{
              borderRadius: 2.5, borderLeft: `4px solid ${theme.palette.warning.main}`,
              transition: 'all 0.2s ease',
              '&:hover': { boxShadow: 2, transform: 'translateX(4px)', borderColor: theme.palette.warning.main },
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CardActionArea onClick={() => navigate(`/documents/flow/${draft.flow_slug}?draft=${draft.id}`)} sx={{ flex: 1, borderRadius: 2 }}>
                <CardContent sx={{ py: compact ? 1.2 : 2, px: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant={compact ? 'body2' : 'subtitle1'} fontWeight={700} sx={{ flex: 1 }}>
                      {draft.flow_name || 'Fluxo'}
                    </Typography>
                    <Chip label={`${draft.current_step + 1}/${draft.total_steps || '?'}`} size="small"
                      sx={{ fontWeight: 600, fontSize: '0.7rem', bgcolor: alpha(theme.palette.warning.main, 0.12), color: 'warning.dark' }} />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" color="text.disabled">
                      {new Date(draft.updated_at).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </Typography>
                  </Box>
                  {!compact && (
                    <Box sx={{ mt: 1.2, width: '100%', bgcolor: alpha(theme.palette.warning.main, 0.12), borderRadius: 1, height: 5 }}>
                      <Box sx={{ width: `${progress}%`, bgcolor: 'warning.main', borderRadius: 1, height: '100%', transition: 'width 0.3s' }} />
                    </Box>
                  )}
                </CardContent>
              </CardActionArea>
              <Stack sx={{ pr: 1.5 }} spacing={0.5}>
                <Tooltip title="Continuar">
                  <IconButton size="small" color="primary" onClick={() => navigate(`/documents/flow/${draft.flow_slug}?draft=${draft.id}`)}
                    sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08), '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.15) } }}>
                    <ArrowForwardIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Excluir">
                  <IconButton size="small" onClick={() => setDeleteDialog(draft.id)}
                    sx={{ '&:hover': { bgcolor: alpha('#f44336', 0.08), color: 'error.main' } }}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>
          </Card>
        );
      })}

      <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)} PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <EditNoteIcon color="error" /> Excluir Rascunho
        </DialogTitle>
        <DialogContent><Typography>Todo o progresso deste rascunho sera perdido. Deseja continuar?</Typography></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialog(null)} sx={{ borderRadius: 2 }}>Cancelar</Button>
          <Button color="error" variant="contained" sx={{ borderRadius: 2 }}
            onClick={() => deleteDialog && handleDelete(deleteDialog)}>Excluir</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default DraftsList;
